import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PermissionPicker } from '@/components/permission-picker';

const createSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  permissions: z.array(z.string()),
});

type CreateFormValues = z.infer<typeof createSchema>;

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateRoleDialog({ open, onOpenChange }: CreateRoleDialogProps) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: catalog } = useQuery({
    queryKey: ['permissions-catalog-flat'],
    queryFn: () => rolesApi.listPermissions(),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', description: '', permissions: [] },
  });

  useEffect(() => {
    if (open) {
      reset({ name: '', description: '', permissions: [] });
      setSubmitError(null);
    }
  }, [open, reset]);

  const mutation = useMutation({
    mutationFn: (values: CreateFormValues) =>
      rolesApi.create({
        name: values.name,
        description: values.description || undefined,
        permissions: values.permissions,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['permissions-catalog'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setSubmitError(err.message || 'Something went wrong.'),
  });

  const onSubmit = (values: CreateFormValues) => {
    setSubmitError(null);
    mutation.mutate(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create custom role</DialogTitle>
          <DialogDescription>
            Custom roles are visible only within your tenant. Pick the permissions
            this role should grant.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-role-name">Name</Label>
            <Input
              id="create-role-name"
              placeholder="e.g. Safety Lead"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              A <span className="font-mono">custom:</span> prefix is added
              automatically to the role key.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-role-description">Description (optional)</Label>
            <Input
              id="create-role-description"
              placeholder="What does this role do?"
              {...register('description')}
            />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <Controller
              name="permissions"
              control={control}
              render={({ field }) => (
                <PermissionPicker
                  permissions={catalog?.data || []}
                  selected={field.value}
                  onChange={field.onChange}
                  disabled={mutation.isPending}
                />
              )}
            />
          </div>

          {submitError && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2">
              {submitError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
